import React from "react";

import useModalState, { ModalState } from "common/ui/useModalState";
import { getCommodityBalanceType } from "domain/commodityBalance";
import Contract, { ContractType } from "domain/contract";
import { isAgrisErp } from "domain/erpSystem";
import { FormField, getSetting } from "domain/formFieldSetting";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import useFormFieldSettings from "features/merchants/useFormFieldSettings";
import { allowAddBasisOffer, allowAddOfferOrContract, allowConvertContract, allowEdit, allowSetBasis, allowSetFutures, isContractTypeAllowed, isRollAllowed } from "./helpers";
import useStoreSelector from "common/redux/useStoreSelector";
import { useErpIntegrationLoader } from "features/merchants/useErpIntegration";
import useOfferings from "features/settings/offerings/useOfferings";
import { ConfirmationModalController, useConfirmationModal } from "common/ui/ConfirmationModal";
import usePermission from "common/security/usePermission";
import Permissions from "common/security/permissions";
import DeliveryPeriod, { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import deliveryPeriodService from "features/deliveryPeriods/deliveryPeriod.service";
import AddOfferModel from "features/offers/addOfferModel";
import { getModelForAddBasisOffer, getModelForConvertBushelOnly, getModelForDelayedPrice, getModelForSetBasis, getModelForSetFutures } from "features/offers/helpers";
import { ContractTableType } from "./ContractTable";
import contractTypeHelper from "domain/contractTypeHelper";
import useMerchantFeature from "features/merchants/useMerchantFeature";
import { isFeatureEnabled, MerchantFeature } from "domain/merchant";
import { ContractSignatureStatus } from "domain/contractSignatureStatus";
import contractService from "./contractService";
import saveBlobFile from "common/saveFile";
import notificationService from "common/notificationService";
import loggingService from "common/logging/loggingService";
import { getProducerDisplayName } from "features/settings/producers/producer";
import * as ContractReduxActions from "./contracts.actions";
import { useDispatch } from "react-redux";
import { MenuItem } from "common/ui/MenuItem";
import ValidationError from "common/validationError";
import { useHelloSign } from "./useHelloSign";
import { ContractSignerType } from "domain/contractSignerType";

type UseContractActionMenuOptions = {
    onActionCompleted?(): void;
}

export default function useContractActionMenu(options: UseContractActionMenuOptions = {}): ContractActionMenuHook {
    const activeMerchant = useActiveMerchant();
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const userConfiguration = useStoreSelector(m => m.userState.userConfiguration);
    const erpIntegrationLoader = useErpIntegrationLoader();
    const allowContractPricing = erpIntegrationLoader.data?.erpConfiguration?.allowContractPricing;
    const offerings = useOfferings();

    const addOfferState = useModalState<{ model: AddOfferModel, contractType: ContractType, basisOffer?: boolean }>();
    const changeQuantityState = useModalState<{ contract: Contract, isSupervisorEditing: boolean }>();
    const editContractState = useModalState<Contract>();
    const contractSignatureState = useModalState<Contract>();
    const contractSignatureWithdrawState = useModalState<Contract>();
    const rollState = useModalState<Contract>();
    const splitState = useModalState<Contract>();
    const resyncState = useModalState<{ contract: Contract, hideRetrySync: boolean }>();

    const formFieldSettings = useFormFieldSettings();
    const quantityFieldSetting = getSetting(formFieldSettings, FormField.Quantity);

    const confirmModal = useConfirmationModal();
    const dispatch = useDispatch();

    const canEditContracts = usePermission(Permissions.EditContracts);
    const canPushBasis = usePermission(Permissions.PushBasis);
    const canPlaceHedge = usePermission(Permissions.PlaceLimitOrder);
    const canSupervisorEdit = usePermission(Permissions.SupervisorEditContract);
    const canEnterErpId = usePermission(Permissions.EnterErpId);
    const canEsign = useMerchantFeature(MerchantFeature.ESignature);

    const { openHelloSign } = useHelloSign({
        onFinish: () => {
            notificationService.success("Contract successfully signed!");
        }
    });

    // Split is disabled for BushelOnly for now.
    function displaySplitContract(contractTableType: ContractTableType) {
        return isFeatureEnabled(activeMerchant, MerchantFeature.SplitContract)
            && canEditContracts && contractTableType !== ContractTableType.BushelOnly;
    }

    const isQuantityFieldEnabled = quantityFieldSetting?.isVisible && quantityFieldSetting?.isEditable;
    const basisOffersEnabled = useMerchantFeature(MerchantFeature.BasisOffers);

    function canArchiveContract(contract: Contract): boolean {
        return contractTypeHelper.isCash(contract.contractType) && (isCustomer || canEditContracts);
    }

    function onEditingContract(contract: Contract, isSupervisorEditing: boolean) {
        editContractState.show({ ...contract, isSupervisorEditing: isSupervisorEditing });
    }

    function showChangeQuantity(contract: Contract, isSupervisorEditing: boolean) {
        changeQuantityState.show({ contract, isSupervisorEditing })
    }

    async function convertContract(contract: Contract) {
        addOfferState.show({ model: getModelForConvertBushelOnly(contract), contractType: contract.contractType });
    }

    function priceDelayedPrice(contract: Contract) {
        addOfferState.show({ model: getModelForDelayedPrice(contract), contractType: contract.contractType });
    }

    async function getDeliveryPeriod(id: string): Promise<DeliveryPeriod> {
        return await deliveryPeriodService.getDeliveryPeriod(id);
    }

    async function placeSetBasis(contract: Contract, { asOffer = false }) {
        if (!canPushBasis && contract.offer.currentBasis == null) {
            confirmModal.show(
                "Set Basis Not Allowed",
                <>There is no basis value for delivery period <strong> {getDeliveryPeriodDisplayName(contract.deliveryPeriod)} </strong>.
                    Please consult with your head merchandiser.</>,
                undefined,
                undefined,
                true
            );
            return;
        }

        const deliveryPeriod = await getDeliveryPeriod(contract.deliveryPeriod?.id);

        const model = asOffer ? getModelForAddBasisOffer(contract) : await getModelForSetBasis(contract, deliveryPeriod)
        addOfferState.show({ model, contractType: contract.contractType, basisOffer: asOffer })
    }

    async function placeFixFutures(contract: Contract) {
        const deliveryPeriod = await getDeliveryPeriod(contract.deliveryPeriod?.id);
        addOfferState.show({ model: await getModelForSetFutures(contract, deliveryPeriod), contractType: contract.contractType });
    }

    function rollContract(contract: Contract) {
        rollState.show(contract);
    }

    function splitContract(contract: Contract) {
        splitState.show(contract);
    }

    async function onErpIdEntered(contract: Contract) {
        resyncState.show({ contract, hideRetrySync: true });
    }

    function onRequestContractSignature(contract: Contract) {
        contractSignatureState.show(contract);
    }

    function onWithdrawContractSignature(contract: Contract) {
        contractSignatureWithdrawState.show(contract);
        confirmModal.show(
            "Withdraw Contract Singature?",
            <>Withdrawing the contract signature request, will prevent the customer from signing it anymore.</>,
            async () => {
                await contractService.withdrawContractSignature(contract.internalId, contract.eSignatureId);
                notificationService.success("Contract signature withdrawn");
                options.onActionCompleted?.();
            }
        );
    }

    async function onSignatureRequested(contract: Contract): Promise<void> {
        try {
            const claimUrl = await contractService.getContractSigningUrl(contract.internalId, contract.eSignatureId);
            openHelloSign(claimUrl);
        }
        catch (error) {
            notificationService.error(((error as ValidationError).errors ?? ["An unknown error occurred"])[0]);
        }
    }

    function closeContract(contract: Contract) {
        confirmModal.show(
            "Close Contract?",
            <>Closing out the contract <strong>#{contract.displayId}</strong> will zero out the remaining quantity.</>,
            async () => {
                await ContractReduxActions.closeContract(contract)(dispatch);
                options.onActionCompleted?.();
            }
        );
    }

    function archiveContract(contract: Contract) {
        confirmModal.show(
            "Archive Contract?",
            <>Archiving the contract will hide the contract from the Open display.</>,
            async () => {
                await ContractReduxActions.archiveContract(contract)(dispatch);
                options.onActionCompleted?.();
            }
        );
    }

    function unarchiveContract(contract: Contract) {
        confirmModal.show(
            "Un-Archive Contract?",
            <>Un-Archiving the contract will move the contract back to the Open display.</>,
            async () => {
                await ContractReduxActions.unarchiveContract(contract)(dispatch);
                options.onActionCompleted?.();
            }
        );
    }

    async function onFileDownloadLinkClicked(contract: Contract): Promise<void> {
        try {
            const fileResponse = await contractService.downloadContractSignatureFile(contract.internalId, contract.eSignatureId);

            saveBlobFile(fileResponse, `Contract-${contract.erpDisplayId ?? contract.displayId}-${getProducerDisplayName(contract.producer)}-signed.pdf`);
        }
        catch (err) {
            notificationService.error("Unable to download file");
            loggingService.error("Unable to download file", err as Error);
        }
    }

    function listActionsForContract(contract: Contract, contractTableType: ContractTableType): MenuItem[] {
        const menuItems: MenuItem[] = [];

        const disablePricingMenu = contract.disablePricings && !allowContractPricing
        const isBasisOrHta = contractTypeHelper.isHta(contract.contractType) || contract.contractType === ContractType.Basis;
        const canRoll = isRollAllowed(isCustomer, isBasisOrHta, contractTableType, canPlaceHedge, canEditContracts, canPushBasis);

        if (!isCustomer && !isContractTypeAllowed(contract.contractType, userConfiguration)) {
            return [{
                name: "Cannot modify this contract",
                disabled: true
            }];
        }

        if (!isCustomer && allowEdit(contract, userConfiguration)) {
            menuItems.push({
                name: "Edit Contract",
                action: () => onEditingContract(contract, false),
                disabled: contract.isReadOnly,
                tooltip: contract.isReadOnly ? "Read-only contracts cannot be edited" : null
            });

            const isContractAppliedInAgris = isAgrisErp(activeMerchant.erpSystem) && getCommodityBalanceType(contract.deliveryMethod) != null;
            if (isQuantityFieldEnabled &&
                (contract.canEditQuantity && !isContractAppliedInAgris)) {
                menuItems.push({
                    name: "Change Quantity",
                    action: () => showChangeQuantity(contract, false),
                    disabled: contract.isReadOnly,
                    tooltip: contract.isReadOnly ? "Read-only contracts cannot be edited" : null
                })
            }
        }

        if (allowSetBasis(contract, offerings, isCustomer, userConfiguration, allowContractPricing)) {
            menuItems.push({
                name: "Set Basis",
                action: () => placeSetBasis(contract, { asOffer: false }),
                disabled: disablePricingMenu,
                tooltip: disablePricingMenu ? "Cannot set basis on this contract" : null,
            },)
        }

        if (allowAddBasisOffer(contract, offerings, isCustomer, userConfiguration, basisOffersEnabled)) {
            menuItems.push({
                name: "Add Basis Offer",
                action: () => placeSetBasis(contract, { asOffer: true }),
                disabled: contract.disablePricings && !allowContractPricing,
                tooltip: contract.disablePricings && !allowContractPricing ? "Cannot set basis on this contract" : null
            });
        }

        if (allowSetFutures(contract, offerings, isCustomer, userConfiguration, allowContractPricing)) {
            menuItems.push({
                name: "Set Futures",
                action: () => placeFixFutures(contract),
                disabled: contract.disablePricings && !allowContractPricing,
                tooltip: contract.disablePricings && !allowContractPricing ? "Cannot set futures on this contract" : null
            });
        }

        if (allowConvertContract(contract, isCustomer)) {
            menuItems.push({
                name: "Convert Contract",
                disabled: contract.disablePricings && !allowContractPricing,
                tooltip: contract.disablePricings && !allowContractPricing ? "Cannot convert this contract" : null,
                action: () => convertContract(contract)
            });
        }

        if (allowAddOfferOrContract(contract, offerings, isCustomer, userConfiguration)) {
            menuItems.push({
                name: "Add Offer/Contract",
                disabled: contract.disablePricings && !allowContractPricing,
                tooltip: contract.disablePricings && !allowContractPricing ? "Cannot add offer/contract on this contract" : null,
                action: () => priceDelayedPrice(contract)
            });
        }

        if (canRoll && contract.remainingQuantity > 0) {
            let disabled = false;
            let tooltip: string = null;
            if (contract.isReadOnly || contract.isRollInProgress) {
                disabled = true;
                tooltip = contract.isReadOnly ? "Read-only contracts cannot be rolled" : "Contract is currently being rolled";
            }

            menuItems.push({
                name: "Roll Contract",
                action: () => rollContract(contract),
                disabled: disabled,
                tooltip: tooltip
            });
        }

        if (displaySplitContract(contractTableType)) {
            menuItems.push({
                name: "Split Contract",
                action: () => splitContract(contract),
                disabled: contract.isReadOnly,
                tooltip: contract.isReadOnly ? "Read-only contracts cannot be split" : "Split this contract into multiple contracts with smaller quantities"
            });
        }

        if (canEnterErpId && activeMerchant.hasErpIntegration) {
            menuItems.push({
                name: "Enter ERP ID",
                action: () => onErpIdEntered(contract),
                tooltip: "Manually set or change the contract's ERP ID. This will not trigger an ERP sync, and is intended for use in situations where the ERP ID cannot be automatically captured or updated through normal sync processes."
            });
        }

        if (canEsign && !isCustomer) {
            if (contract.eSignatureStatus === ContractSignatureStatus.Sent) {
                if (contract.eSignatureAwaitingSignerType === ContractSignerType.Merchant) {
                    menuItems.push({
                        name: "Sign Contract",
                        action: () => onSignatureRequested(contract),
                        tooltip: "Sign this contract"
                    });
                }
                menuItems.push({
                    name: "Withdraw Signature Request",
                    action: () => onWithdrawContractSignature(contract),
                    tooltip: "Withdraw Signature for this contract, customer will not be able to sign it"
                });
            } else {
                menuItems.push({
                    name: "Request Signature",
                    action: () => onRequestContractSignature(contract),
                    disabled: contract.customerAccount?.accountId == null,
                    tooltip: "Request Contract signature for this contract, system will send it to the customer"
                });
            }
        }

        if (canSupervisorEdit) {
            menuItems.push({
                name: "Supervisor Edit",
                action: () => onEditingContract(contract, true),
                tooltip: "Read-only contracts can be edited, but changes will not be sent to your ERP"
            });

            if (isQuantityFieldEnabled) {
                menuItems.push({
                    name: "Supervisor Change Quantity",
                    action: () => showChangeQuantity(contract, true),
                    tooltip: "Read-only contracts can be edited but changes will not be sent to your ERP"
                })
            }
        }

        if (isBasisOrHta && canEditContracts && contract.remainingQuantity > 0) {
            menuItems.push({
                name: "Close Contract",
                action: () => closeContract(contract)
            });
        }

        if (canArchiveContract(contract) && !contract.isArchived) {
            menuItems.push({
                name: "Archive Contract",
                action: () => archiveContract(contract)
            });
        }

        if (canArchiveContract(contract) && contract.isArchived) {
            menuItems.push({
                name: "Un-Archive Contract",
                action: () => unarchiveContract(contract)
            });
        }

        if (canEsign && contract.eSignatureStatus === ContractSignatureStatus.Signed) {
            menuItems.push({
                name: "View Signed Contract",
                action: () => onFileDownloadLinkClicked(contract)
            });
        }

        return menuItems;
    }

    return {
        listActionsForContract,
        addOfferState,
        changeQuantityState,
        editContractState,
        contractSignatureState,
        contractSignatureWithwdrawState: contractSignatureWithdrawState,
        rollState,
        splitState,
        resyncState,
        confirmModal
    }
}

export interface ContractActionMenuHook {
    listActionsForContract(contract: Contract, contractTableType: ContractTableType): MenuItem[];

    addOfferState: ModalState<{ model: AddOfferModel, contractType: ContractType, basisOffer?: boolean }>;
    changeQuantityState: ModalState<{ contract: Contract, isSupervisorEditing: boolean }>;
    editContractState: ModalState<Contract>;
    contractSignatureState: ModalState<Contract>;
    contractSignatureWithwdrawState: ModalState<Contract>;
    rollState: ModalState<Contract>;
    splitState: ModalState<Contract>;
    resyncState: ModalState<{ contract: Contract, hideRetrySync: boolean }>;

    confirmModal: ConfirmationModalController;
}